import { Component, Inject, Injector, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { DateRange } from "src/app/models/dateRange.model";
import {
    NotificationSettings,
    ShiftRange,
} from "src/app/models/notificationSettings.model";
import { NotificationSettingsService } from "src/app/services/notificationSettings.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    FormComponent,
    IFormDialogData,
} from "src/app/shared/form/form.component";
import {
    ArrayFormField,
    ButtonFormField,
    CheckboxFormField,
    FormField,
    HierarchicalSelectionFormField,
    MultiSelectionFormField,
    NumberFormField,
    SingleSelectionFormField,
    TextAreaFormField,
    TextFormField,
    TimeFormField,
    ToggleFormField,
} from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import { Lookup } from "src/app/models/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { ParameterService } from "src/app/services/parameter.service";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { NotificationAveragingEnum } from "src/app/enums/notificationAveraging.enum";
import { AlertSeverityLevel } from "src/app/enums/alert.enum";
import { ValueConversionService } from "src/app/services/value-conversion.service";
import { ParameterLookup } from "src/app/models/parameter.model";
import { StepConfig, StepperConfig } from "src/app/shared/stepper/stepper.model";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { ControlService } from "src/app/services/control.service";

@Component({
  standalone: false,
    selector: 'app-notification-settings-dialog',
    templateUrl: './notification-settings-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./notification-settings-dialog.component.scss'],
})
export class NotificationSettingsDialogComponent
    extends FormComponent<NotificationSettings>
    implements OnInit
{
    @ViewChild('nsStepTpl', { static: true }) nsStepTpl!: TemplateRef<unknown>;

    static readonly DEFAULT_HYSTERESIS_SECONDS = 120;
    static readonly DEFAULT_AUTO_ESCALATE_MINUTES = 30;
    static readonly DEFAULT_EVALUATION_CADENCE_MINUTES = 15;

    parameters: ParameterLookup[];
    organizationsList: OrganizationLevel[];
    averagingTypesList: ILookup[];
    daysOfWeekList: ILookup[];
    limitTypesList: ILookup[];
    conditionTypesList: ILookup[];
    adminsList: ILookup[];
    severitiesList: ILookup[];
    sourceRulesList: Lookup[];
    controlsList: ILookup[];

    stepperConfig: StepperConfig;
    currentStep: StepConfig;
    triggerFields: FormField[] = [];
    scheduleFields: FormField[] = [];
    alertingFields: FormField[] = [];
    recipientFields: FormField[] = [];

    constructor(
        public dialogRef: MatDialogRef<NotificationSettingsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        protected injector: Injector,
        protected notificationSettingsService: NotificationSettingsService,
        protected dateFormatService: DateFormatService,
        protected parameterService: ParameterService,
        protected organizationLevelService: OrganizationLevelService,
        protected valueConversionService: ValueConversionService,
        protected controlService: ControlService
    ) {
        super(
            dialogRef,
            notificationSettingsService,
            data,
            "Notification Settings",
            injector
        );
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            options:
                this.notificationSettingsService.getNotificationSettingsData(),
            parameters: this.parameterService.getAll(),
            organizations: this.organizationLevelService.getAll(),
            rules: this.notificationSettingsService.getAll(),
            controls: this.controlService.getAll(),
        }).subscribe({
            next: (result) => {
                this.entity = result.entity;
                this.parameters = result.parameters;
                this.organizationsList = result.organizations;
                const toLookup = (list: { id: number; value: string }[]) =>
                    (list ?? []).map((val) => ({ id: val.id, name: val.value }));
                this.averagingTypesList = toLookup(result.options.averagingTypes).map((item) =>
                    item.id === NotificationAveragingEnum.TimeWeightedAverage
                        ? { ...item, name: this.translateService.getDynamicTranslation("Shift Time weighted average") }
                        : item
                );
                this.daysOfWeekList = toLookup(result.options.daysOfWeek);
                this.limitTypesList = toLookup(result.options.limitTypes);
                this.conditionTypesList = toLookup(result.options.conditionTypes);
                this.severitiesList = toLookup(result.options.severities);
                this.adminsList = (result.options.admins ?? []).map((val) => ({
                    id: val.id,
                    name: val.userName,
                }));
                this.sourceRulesList = (result.rules ?? [])
                    .filter((rule) => rule.id !== this.entity.id)
                    .map((rule) => ({ id: rule.id, name: rule.name }));
                this.controlsList = result.controls ?? [];
                this.initModel();
                this.initFields();
                this.buildStepperConfig();
                this.isFormLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        });
    }

    initModel() {
        this.entity.shifts = this.entity.shifts || [];
        this.setCurrentParameterType();
        if (this.entity.threshold && this.entity.parameterType != null) {
            this.entity.threshold =
                this.valueConversionService.getParameterValueFromUserPreferences(
                    this.entity.threshold,
                    this.entity.parameterType
                );
        }
        this.entity.timePeriod = this.valueConversionService.convertSecondsToMinutes(this.entity?.timePeriod ?? 0);
        // Blank means "use the backend default" — show the effective value rather than an empty box (hysteresis is stored in seconds, presented in minutes).
        this.entity.hysteresisSeconds = this.valueConversionService.convertSecondsToMinutes(
            this.entity.hysteresisSeconds ??
                NotificationSettingsDialogComponent.DEFAULT_HYSTERESIS_SECONDS
        );
        this.entity.autoEscalateAfterMinutes =
            this.entity.autoEscalateAfterMinutes ??
            NotificationSettingsDialogComponent.DEFAULT_AUTO_ESCALATE_MINUTES;
        this.entity.controlIds = this.entity.controlIds ?? [];
        this.entity.isCadenceEnabled = this.entity.evaluationCadenceMinutes != null;
    }

    initFields() {
        const shifts = this.initShifts();
        const labelWithUnit = () => {
            return this.entity.parameterType != null ? `(${this.valueConversionService.getParameterUnit(this.entity.parameterType)})` : "";
        }

        this.triggerFields = [
            new TextFormField({
                property: nameOf<NotificationSettings>((x) => x.name),
                name: "Name",
                columnSpan: new ColumnSpan(1, 2),
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.organizationLevelId),
                name: "Organization Level",
                options: toOrganizationTree(this.organizationsList),
                columnSpan: new ColumnSpan(1, 2),
                required: true,
            }),
            new ToggleFormField({
                property: nameOf<NotificationSettings>((x) => x.isEnabled),
                name: "Status",
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
            new SingleSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.parameterId),
                name: "Parameter Name",
                options: this.parameters,
                columnSpan: new ColumnSpan(1, 2),
                onChange: () => this.setCurrentParameterType(),
            }),
            new SingleSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.averagingType),
                name: "Averaging Type",
                options: this.averagingTypesList,
                columnSpan: new ColumnSpan(1, 2),
                onChange: () => this.updateRepeatedExceedanceState(),
            }),
            new NumberFormField({
                property: nameOf<NotificationSettings>((x) => x.timePeriod),
                name: "Time [minutes]",
                columnSpan: new ColumnSpan(1, 2),
                min: 0,
                max: 180,
                required: false,
                isVisible: (entity: NotificationSettings) =>
                    entity.averagingType != NotificationAveragingEnum.Instantaneous &&
                    entity.averagingType != NotificationAveragingEnum.TimeWeightedAverage &&
                    entity.averagingType != NotificationAveragingEnum.RepeatedExceedance,
            }),
            new SingleSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.conditionType),
                name: "Condition Type",
                options: this.conditionTypesList,
                columnSpan: new ColumnSpan(1, 2),
                isVisible: (entity: NotificationSettings) =>
                    entity.averagingType != NotificationAveragingEnum.RepeatedExceedance,
            }),
            new NumberFormField({
                property: nameOf<NotificationSettings>((x) => x.threshold),
                name: "Threshold",
                columnSpan: new ColumnSpan(1, 2),
                additionalLabel: () => labelWithUnit(),
                isVisible: (entity: NotificationSettings) =>
                    entity.averagingType != NotificationAveragingEnum.RepeatedExceedance,
            }),
            new CheckboxFormField({
                property: nameOf<NotificationSettings>((x) => x.applyFogCorrection),
                name: "Fog/Rain Detection",
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
            new SingleSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.sourceRuleId),
                name: "Source Rule",
                tooltip: this.translateService.getDynamicTranslation("The existing rule whose breaches are counted for repeated-exceedance detection."),
                options: this.sourceRulesList,
                columnSpan: new ColumnSpan(1, 2),
                isVisible: (entity: NotificationSettings) =>
                    entity?.averagingType === NotificationAveragingEnum.RepeatedExceedance,
            }),
            new NumberFormField({
                property: nameOf<NotificationSettings>((x) => x.repeatCountThreshold),
                name: "Repeat Count Threshold",
                columnSpan: new ColumnSpan(1, 2),
                min: 1,
                isVisible: (entity: NotificationSettings) =>
                    entity?.averagingType === NotificationAveragingEnum.RepeatedExceedance,
            }),
            new NumberFormField({
                property: nameOf<NotificationSettings>((x) => x.repeatWindowMinutes),
                name: "Repeat Window [minutes]",
                columnSpan: new ColumnSpan(1, 2),
                min: 1,
                isVisible: (entity: NotificationSettings) =>
                    entity?.averagingType === NotificationAveragingEnum.RepeatedExceedance,
            }),
        ];

        this.scheduleFields = [
            new MultiSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.workingDays),
                name: "Working Days",
                options: this.daysOfWeekList,
                columnSpan: new ColumnSpan(1, 1),
            }),
            new ButtonFormField({
                name: "Add Shift",
                property: nameOf<NotificationSettings>((x) => x.id),
                columnSpan: new ColumnSpan(1, 3),
                iconClass: "fa fa-plus",
                required: false,
                onClick: () => this.addShift(),
            }),
            new ArrayFormField({
                property: nameOf<NotificationSettings>((x) => x.shifts),
                name: "Shifts",
                items: shifts,
                cssClass: "multiple-rows",
                columnSpan: new ColumnSpan(1, 1),
                required: false,
            }),
        ];

        this.alertingFields = [
            new SingleSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.severity),
                name: "Severity",
                tooltip: this.translateService.getDynamicTranslation("Severity stamped on generated alert events. Drives the History severity badge and minimum-severity filter."),
                options: this.severitiesList,
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
            new NumberFormField({
                property: nameOf<NotificationSettings>((x) => x.limit),
                name: "Notification Limit",
                columnSpan: new ColumnSpan(1, 2),
            }),
            new SingleSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.limitType),
                name: "Limit Type",
                options: this.limitTypesList,
                columnSpan: new ColumnSpan(1, 2),
            }),
            new NumberFormField({
                property: nameOf<NotificationSettings>((x) => x.hysteresisSeconds),
                name: "Hysteresis [minutes]",
                tooltip: this.translateService.getDynamicTranslation("Dwell time an alert must stay below threshold before auto-resolving. Leave blank to use the default (2 minutes)."),
                columnSpan: new ColumnSpan(1, 2),
                min: 0,
                required: false,
            }),
            new NumberFormField({
                property: nameOf<NotificationSettings>((x) => x.autoEscalateAfterMinutes),
                name: "Auto-escalate After [minutes]",
                tooltip: this.translateService.getDynamicTranslation("Minutes an active alert may remain unacknowledged before it auto-escalates. Leave blank to use the default (30 minutes)."),
                columnSpan: new ColumnSpan(1, 2),
                min: 0,
                required: false,
            }),
            new TextAreaFormField({
                property: nameOf<NotificationSettings>((x) => x.responseAction),
                name: "Response Action",
                tooltip: this.translateService.getDynamicTranslation("Prose guidance shown alongside the response controls below."),
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
            new MultiSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.controlIds),
                name: "Response Controls",
                tooltip: this.translateService.getDynamicTranslation("The controls this rule's response action references. Used to rank relevant controls first on the response page."),
                options: this.controlsList,
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
            new ToggleFormField({
                property: nameOf<NotificationSettings>((x) => x.isCadenceEnabled),
                name: "Evaluation Cadence",
                tooltip: this.translateService.getDynamicTranslation("Optional. Groups this rule's evaluation across multiple nodes on a fixed interval. Leave off to keep the rule continuous and evaluated per node."),
                columnSpan: new ColumnSpan(1, 2),
                required: false,
                onChange: () => this.updateCadenceState(),
            }),
            new NumberFormField({
                property: nameOf<NotificationSettings>((x) => x.evaluationCadenceMinutes),
                name: "Evaluate Every [minutes]",
                columnSpan: new ColumnSpan(1, 2),
                min: 1,
                isVisible: (entity: NotificationSettings) => entity?.isCadenceEnabled === true,
            }),
        ];

        this.recipientFields = [
            new MultiSelectionFormField({
                property: nameOf<NotificationSettings>((x) => x.subscribers),
                name: "Subscribers",
                options: this.adminsList,
                columnSpan: new ColumnSpan(1, 1),
            }),
        ];

        this.fields = [
            ...this.triggerFields,
            ...this.scheduleFields,
            ...this.alertingFields,
            ...this.recipientFields,
        ];
        this.updateRepeatedExceedanceState();
        this.updateCadenceState();
    }

    get isRepeatedExceedance(): boolean {
        return this.entity?.averagingType === NotificationAveragingEnum.RepeatedExceedance;
    }

    get shiftsField(): FormField {
        return this.scheduleFields.find(
            (f) => f.property === nameOf<NotificationSettings>((x) => x.shifts)
        );
    }

    get nonShiftScheduleFields(): FormField[] {
        const shiftsProperty = nameOf<NotificationSettings>((x) => x.shifts);
        return this.scheduleFields.filter((f) => f.property !== shiftsProperty);
    }

    private updateRepeatedExceedanceState() {
        const isRe = this.isRepeatedExceedance;
        const reProperties: string[] = [
            nameOf<NotificationSettings>((x) => x.sourceRuleId),
            nameOf<NotificationSettings>((x) => x.repeatCountThreshold),
            nameOf<NotificationSettings>((x) => x.repeatWindowMinutes),
        ];
        // Fields hidden in Repeated Exceedance mode must drop their required flag,
        // otherwise isDisabled() (which ignores visibility) would block saving.
        const nonReProperties: string[] = [
            nameOf<NotificationSettings>((x) => x.threshold),
            nameOf<NotificationSettings>((x) => x.conditionType),
        ];
        this.triggerFields.forEach((field) => {
            const prop = field.property as string;
            if (reProperties.includes(prop)) field.required = isRe;
            if (nonReProperties.includes(prop)) field.required = !isRe;
        });
        if (isRe) {
            // Threshold and condition type do not apply in Repeated Exceedance mode; clear them symmetrically so no stale value is persisted.
            this.entity.threshold = null;
            this.entity.conditionType = null;
        } else {
            this.entity.sourceRuleId = null;
            this.entity.repeatCountThreshold = null;
            this.entity.repeatWindowMinutes = null;
        }
        // Time period does not apply to Shift Time weighted average, so drop any stale value.
        if (this.entity.averagingType === NotificationAveragingEnum.TimeWeightedAverage) {
            this.entity.timePeriod = null;
        }
        this.updateFieldVisibility();
    }

    private updateCadenceState() {
        const cadenceProperty = nameOf<NotificationSettings>((x) => x.evaluationCadenceMinutes);
        this.alertingFields.forEach((field) => {
            if (field.property === cadenceProperty) field.required = this.entity.isCadenceEnabled;
        });
        if (this.entity.isCadenceEnabled) {
            this.entity.evaluationCadenceMinutes =
                this.entity.evaluationCadenceMinutes ??
                NotificationSettingsDialogComponent.DEFAULT_EVALUATION_CADENCE_MINUTES;
        } else {
            // Unset cadence keeps the rule continuous and per-node — clear any stale value.
            this.entity.evaluationCadenceMinutes = null;
        }
        this.updateFieldVisibility();
    }

    private buildStepperConfig() {
        const cancelButton = {
            label: 'Cancel',
            class: 'transparent-btn bordered-btn px-5',
            action: () => this.close(),
            disabled: () => this.isLoading,
        };
        const saveButton = {
            label: this.isAdd ? 'Create' : 'Save Changes',
            class: 'custom-main-btn px-5',
            action: () => this.save(),
            disabled: () => this.isLoading || this.cachedDisabled,
            loading: () => this.isLoading,
            loadingLabel: 'Saving changes…',
        };
        const stepButtons = (isLast: boolean) =>
            this.isAdd && !isLast ? [cancelButton] : [cancelButton, saveButton];

        this.stepperConfig = {
            orientation: 'horizontal',
            linear: this.isAdd,
            steps: [
                {
                    id: 'trigger',
                    label: 'Trigger',
                    template: this.nsStepTpl,
                    fields: this.triggerFields,
                    canProceed: () => this.isTriggerValid(),
                    buttons: stepButtons(false),
                },
                {
                    id: 'schedule',
                    label: 'Schedule',
                    template: this.nsStepTpl,
                    fields: this.scheduleFields,
                    canProceed: () => true,
                    buttons: stepButtons(false),
                },
                {
                    id: 'alerting',
                    label: 'Alerting & Lifecycle',
                    template: this.nsStepTpl,
                    fields: this.alertingFields,
                    canProceed: () => true,
                    buttons: stepButtons(false),
                },
                {
                    id: 'recipients',
                    label: 'Recipients',
                    template: this.nsStepTpl,
                    fields: this.recipientFields,
                    canProceed: () => true,
                    buttons: stepButtons(true),
                },
            ],
        };
        this.currentStep = this.stepperConfig.steps[0];
    }

    onStepChanged(step: StepConfig) {
        this.currentStep = step;
    }

    isTriggerValid(): boolean {
        if (!this.entity?.name || !this.entity?.organizationLevelId ||
            !this.entity?.parameterId || this.entity?.averagingType == null) return false;
        if (this.isRepeatedExceedance) {
            return this.entity.sourceRuleId != null &&
                this.entity.repeatCountThreshold != null &&
                this.entity.repeatWindowMinutes != null;
        }
        return this.entity.conditionType != null && this.entity.threshold != null;
    }

    setCurrentParameterType() {
        const parameter = this.parameters.find(
            (type) => type.id === this.entity.parameterId
        );
        this.entity.parameterType = parameter ? parameter?.parameterType : null;
        this.updateFieldVisibility();
    }

    initShifts(): FormField[][] {
        const shiftFields: FormField[][] = [];
        if (this.entity.shifts.length === 0) {
            shiftFields.push(this.createShiftFields());
            const currentTime = this.dateFormatService.formatDateTimeToString(
                new Date(),
                this.dateFormatService.dateToTimeFormat
            );
            this.entity.shifts.push(
                new ShiftRange(0, 0, currentTime, currentTime)
            );
        } else {
            this.entity.shifts = this.entity.shifts.map((shift) => {
                shiftFields.push(this.createShiftFields());
                const startDate =
                    this.dateFormatService.formatTimestampToString(
                        shift[0],
                        this.dateFormatService.dateToTimeFormat
                    );
                const endDate = this.dateFormatService.formatTimestampToString(
                    shift[1],
                    this.dateFormatService.dateToTimeFormat
                );
                return new ShiftRange(0, 0, startDate, endDate);
            });
        }
        return shiftFields;
    }

    createShiftFields(): FormField[] {
        return [
            new TimeFormField({
                property: nameOf<DateRange>((x) => x.startDate),
                name: "Start Time",
                columnSpan: new ColumnSpan(2, 5),
                cssClass: "multiple-row",
            }),
            new TimeFormField({
                property: nameOf<DateRange>((x) => x.endDate),
                name: "End Time",
                columnSpan: new ColumnSpan(2, 5),
                cssClass: "multiple-row",
            }),
        ];
    }

    addShift() {
        if (this.entity.shifts.length >= 4) {
            this.toastService.showError("", "A maximum of 4 shifts can be added");
            return;
        }
        this.shiftsField?.items.push(this.createShiftFields());
        this.entity.shifts.push(new ShiftRange());
        this.updateFieldVisibility();
    }

    removeShift(index: number) {
        this.shiftsField?.items.splice(index, 1);
        this.entity.shifts.splice(index, 1);
        this.updateFieldVisibility();
    }

    onShiftTimeChange(event: string, index: number, fieldItem: FormField) {
        this.onTimeChange(event, this.shiftsField, fieldItem, index);
    }

    onBeforeSave(model: NotificationSettings): void {
        model.applyFogCorrection = model.applyFogCorrection ?? false;

        const formattedDateString = (time) => {
            const [hours, minutes] = time.split(":").map(Number);
            const now = new Date();
            now.setHours(hours);
            now.setMinutes(minutes);
            return now;
        };
        model.shifts = model.shifts.map((shift: any) => {
            shift.startDate = this.dateFormatService.convertDateToTimestamp(
                formattedDateString(shift.startDate)
            );
            shift.endDate = this.dateFormatService.convertDateToTimestamp(
                formattedDateString(shift.endDate)
            );
            if (shift.endDate < shift.startDate) {
                shift.endDate += this.dateFormatService.SECONDS_IN_DAY;
            }
            shift = [shift.startDate, shift.endDate];
            return shift;
        });
        if (model.threshold && this.entity.parameterType != null) {
            model.threshold =
                this.valueConversionService.convertUserPreferredValueToBase(
                    model.threshold,
                    this.entity.parameterType
                );
            model.parameterType = this.entity.parameterType;
        }
        model.timePeriod = this.valueConversionService.convertMinutesToSeconds(model?.timePeriod ?? 0);
        model.hysteresisSeconds = model.hysteresisSeconds != null
            ? this.valueConversionService.convertMinutesToSeconds(model.hysteresisSeconds)
            : null;
        if (model.averagingType !== NotificationAveragingEnum.RepeatedExceedance) {
            model.sourceRuleId = null;
            model.repeatCountThreshold = null;
            model.repeatWindowMinutes = null;
        } else {
            model.threshold = null;
            model.conditionType = null;
        }
        if (typeof model.subscribers === "string") {
            model.subscribers = [model.subscribers as unknown as string];
        }
        if (!model.isCadenceEnabled) {
            model.evaluationCadenceMinutes = null;
        }
        model.isCadenceEnabled = undefined;
    }

    getNewObject(): NotificationSettings {
        const entity = new NotificationSettings();
        entity.severity = AlertSeverityLevel.Alert;
        entity.hysteresisSeconds = NotificationSettingsDialogComponent.DEFAULT_HYSTERESIS_SECONDS;
        entity.autoEscalateAfterMinutes = NotificationSettingsDialogComponent.DEFAULT_AUTO_ESCALATE_MINUTES;
        entity.controlIds = [];
        return entity;
    }

    validate(): boolean {
        if (this.entity.shifts.length > 4) {
            this.toastService.showError("", "A maximum of 4 shifts can be added");
            return false;
        }
        return this.isTriggerValid();
    }

    closeDialog(data: NotificationSettings) {
        this.dialogRef.close(data);
    }
}
